import { createContext, use, useEffect, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { useSiteName } from './site-name'

const PageTitleContext = createContext<(title: string | null) => void>(() => undefined)

/**
 * Sets the browser tab's title to the route's own, or to what the page gives
 * {@link usePageTitle}, such as a record's name, followed by the site's name.
 */
export function RouteTitle(props: { readonly titleId: string; readonly children: ReactNode }) {
  const intl = useIntl()
  const [override, setOverride] = useState<string | null>(null)
  const page = override ?? intl.formatMessage({ id: props.titleId })
  const site = useSiteName() ?? intl.formatMessage({ id: 'shell.defaultSiteName' })

  useEffect(() => {
    document.title = intl.formatMessage({ id: 'shell.documentTitle' }, { page, site })
  }, [intl, page, site])

  return <PageTitleContext value={setOverride}>{props.children}</PageTitleContext>
}

/** Replaces the route's title while the calling page is shown; `undefined` keeps the route's. */
export function usePageTitle(title: string | undefined): void {
  const setOverride = use(PageTitleContext)

  useEffect(() => {
    if (title === undefined) {
      return undefined
    }
    setOverride(title)
    return () => {
      setOverride(null)
    }
  }, [setOverride, title])
}
